import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';

const Page = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 0 20px;
`;

const Header = styled.h1`
  color: var(--text);
  margin-bottom: 16px;
`;

const Form = styled.form`
  display: grid;
  gap: 12px;
  background: var(--surface);
  padding: 20px;
  border: 1px solid var(--border);
  border-radius: 10px;
`;

const Row = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr 1fr;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const Input = styled.input`
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text);
`;

const Textarea = styled.textarea`
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text);
  min-height: 140px;
`;

const Btn = styled.button`
  padding: 12px 18px;
  border-radius: 24px;
  border: none;
  background: var(--accent);
  color: var(--accent-contrast);
  font-weight: 700;
  cursor: pointer;
  width: fit-content;
`;

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const headerRef = useRef(null);
  const formRef = useRef(null);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', text: '' });
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send');
      setStatus({ type: 'ok', text: 'Thanks! We will get back to you soon.' });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (e) {
      setStatus({ type: 'err', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (headerRef.current) gsap.from(headerRef.current, { autoAlpha: 0, y: 16, duration: 1, ease: 'power2.out' });
      if (formRef.current) {
        const fields = formRef.current.querySelectorAll('input, textarea, button');
        gsap.from(fields, { autoAlpha: 0, y: 10, duration: 1, ease: 'power2.out', stagger: 0.06, delay: 0.1 });
      }
    });
    return () => ctx.revert();
  }, []);

  return (
    <Page>
      <Header ref={headerRef}>Contact Us</Header>
      {status.text ? (
        <div style={{ color: status.type === 'ok' ? '#0f766e' : '#b91c1c', marginBottom: 10 }}>{status.text}</div>
      ) : null}
      <Form ref={formRef} onSubmit={onSubmit}>
        <Row>
          <Input name="name" placeholder="Name" value={form.name} onChange={onChange} required />
          <Input name="email" type="email" placeholder="Email" value={form.email} onChange={onChange} required />
        </Row>
        <Input name="subject" placeholder="Subject" value={form.subject} onChange={onChange} />
        <Textarea name="message" placeholder="Message" value={form.message} onChange={onChange} required />
        <Btn type="submit" disabled={loading}>{loading ? 'Sending…' : 'Send Message'}</Btn>
      </Form>
    </Page>
  );
};

export default Contact;
